/**
 * Taxeren met referenties (MA-56).
 *
 * Dit is werkwijze v3 uit de proef van 13 oktober 2026 (Den Haag, zes blinde
 * taxaties in twee rondes, zie docs/taxatie.md) als rekenregels. Er zijn geen
 * gewichten: per kenmerk zegt de gebruiker of een referentie beter, gelijk of
 * slechter is dan het doelpand, en schuift de correctie binnen een vaste band.
 * De banden zijn schattingen; ze horen bijgesteld te worden zodra er echte
 * koopsommen naast de taxaties liggen.
 *
 * Alles hier is puur rekenwerk, zodat API, scherm en tests op dezelfde manier
 * tellen. De API rekent bij het bewaren opnieuw; wat de browser toont is een
 * voorproefje van dezelfde functie.
 */
import { z } from 'zod';

/* --- Kenmerken ------------------------------------------------------------- */

/** Altijd in de vergelijkingstabel. */
export const KERNKENMERKEN = [
  'm2',
  'slaapkamers',
  'buitenruimte',
  'staat',
  'ligging',
  'verdieping',
  'eigendom',
  'energielabel',
  'status',
] as const;

/** Alleen als ze hier de waarde bepalen. */
export const EXTRA_KENMERKEN = ['uitzicht', 'parkeren', 'verkoopwijze', 'plafond'] as const;

export const KENMERKEN = [...KERNKENMERKEN, ...EXTRA_KENMERKEN] as const;
export const kenmerkSchema = z.enum(KENMERKEN);
export type Kenmerk = z.infer<typeof kenmerkSchema>;
export type Kernkenmerk = (typeof KERNKENMERKEN)[number];
export type ExtraKenmerk = (typeof EXTRA_KENMERKEN)[number];

export const KENMERK_LABELS: Record<Kenmerk, string> = {
  m2: 'm²',
  slaapkamers: 'Slaapkamers',
  buitenruimte: 'Buitenruimte',
  staat: 'Staat',
  ligging: 'Ligging',
  verdieping: 'Verdieping en toegang',
  eigendom: 'Eigendom',
  energielabel: 'Energielabel',
  status: 'Status',
  uitzicht: 'Uitzicht',
  parkeren: 'Parkeerplaats',
  verkoopwijze: 'Manier van verkopen',
  plafond: 'Plafondhoogte en ramen',
};

/** Correcties in procenten van de referentieprijs, of in euro's waar dat eerlijker is. */
export const KENMERK_EENHEID: Record<Kenmerk, 'pct' | 'euro'> = {
  m2: 'pct',
  slaapkamers: 'pct',
  buitenruimte: 'pct',
  staat: 'pct',
  ligging: 'pct',
  verdieping: 'pct',
  eigendom: 'euro',
  energielabel: 'pct',
  status: 'pct',
  uitzicht: 'pct',
  parkeren: 'euro',
  verkoopwijze: 'pct',
  plafond: 'pct',
};

/** Waar een oordeel op rust. */
export const BRONNEN = ['data', 'tekst', 'foto', 'kaart'] as const;
export const oordeelBronSchema = z.enum(BRONNEN);

/**
 * "Beter" betekent: de referentie is beter dan het doelpand. Dan gaat er van
 * zijn prijs af, dus de correctie is negatief.
 */
export const OORDELEN = ['beter', 'gelijk', 'slechter', 'onbekend'] as const;
export const oordeelSchema = z.enum(OORDELEN);
export type Oordeel = z.infer<typeof oordeelSchema>;

/* --- Waarden die het doelpand en een referentie dragen --------------------- */

export const BUITENRUIMTES = ['geen', 'balkon', 'tuin_dakterras'] as const;
export const buitenruimteSchema = z.enum(BUITENRUIMTES);
export type Buitenruimte = z.infer<typeof buitenruimteSchema>;

export const STATEN = ['casco', 'gedateerd', 'goed', 'nieuw'] as const;
export const staatSchema = z.enum(STATEN);
export type Staat = z.infer<typeof staatSchema>;

/**
 * Wat de prijs van een referentie is. Alleen `transactie` is een koopsom; de
 * rest is een vraagprijs, en zolang er geen koopsommen zijn staat dat ook op
 * het A4.
 */
export const PRIJSSOORTEN = ['transactie', 'verkocht_ov', 'onder_bod', 'aanbod'] as const;
export const prijssoortSchema = z.enum(PRIJSSOORTEN);
export type Prijssoort = z.infer<typeof prijssoortSchema>;

export const PRIJSSOORT_LABELS: Record<Prijssoort, string> = {
  transactie: 'Koopsom',
  verkocht_ov: 'Verkocht o.v.',
  onder_bod: 'Onder bod',
  aanbod: 'Aanbod',
};

/** Een koopsom of een vraagprijs die al tot een koper leidde. */
export function isTransactieAchtig(soort: Prijssoort): boolean {
  return soort !== 'aanbod';
}

/* --- Vergelijkbaarheid (MA-70) ------------------------------------------------ */

/**
 * Hoe vergelijkbaar een referentie per kenmerk is met het doelpand, van 4
 * (heel vergelijkbaar) tot 0 (helemaal niet). Sinds MA-70 is dat het oordeel:
 * geen correctie omhoog of omlaag meer, maar de vraag of de woning een goede
 * vergelijking is. De waarde volgt uit de prijs per m² van de referenties die je
 * meeneemt; de ondergrens is de laagste daarvan.
 *
 * Een referentie als geheel is het gemiddelde van haar kenmerken, maar hooguit
 * één stap boven haar zwakste: weinig op de staat is hooguit matig in totaal.
 */
export const VERGELIJKBAAR = [
  { waarde: 4, kort: 'heel', lang: 'heel vergelijkbaar' },
  { waarde: 3, kort: 'goed', lang: 'goed vergelijkbaar' },
  { waarde: 2, kort: 'matig', lang: 'matig vergelijkbaar' },
  { waarde: 1, kort: 'weinig', lang: 'weinig vergelijkbaar' },
  { waarde: 0, kort: 'niet', lang: 'helemaal niet vergelijkbaar' },
] as const;
export const vergelijkbaarSchema = z.number().int().min(0).max(4);

/** Wie zo vergelijkbaar is of meer, telt standaard mee; 3 is "goed". */
export const DREMPEL_STANDAARD = 3;

/** "goed vergelijkbaar" bij een (gemiddelde) stand; afgerond op de dichtstbijzijnde. */
export function vergelijkbaarWoord(stand: number | null, lang = false): string {
  if (stand == null) return 'nog niet beoordeeld';
  const gevonden = VERGELIJKBAAR.find((x) => x.waarde === Math.round(stand));
  return gevonden ? (lang ? gevonden.lang : gevonden.kort) : 'nog niet beoordeeld';
}

/* --- Invoer ---------------------------------------------------------------- */

const geheel = z.number().int();

export const kenmerkOordeelSchema = z.object({
  oordeel: oordeelSchema,
  /** Procenten op de referentieprijs, of euro's - zie KENMERK_EENHEID. */
  correctie: z.number().finite().default(0),
  bron: oordeelBronSchema.nullable().default(null),
  notitie: z.string().max(500).default(''),
});
export type KenmerkOordeel = z.infer<typeof kenmerkOordeelSchema>;

export const referentieSoortSchema = z.enum(['transactie', 'woning']);

export const referentieSchema = z.object({
  /** Een verkochte woning uit de transacties, of een woning uit het aanbod. */
  soort: referentieSoortSchema,
  id: z.uuid(),
  adres: z.string().max(200),
  prijs: geheel.positive(),
  prijssoort: prijssoortSchema,
  /** Koopdatum, of de datum van de status; ISO yyyy-mm-dd. */
  datum: z.iso.date().nullable().default(null),
  oppervlakteM2: geheel.positive().nullable().default(null),
  /** Factor van de kwartaalindex tot vandaag; zonder index telt een oude verkoop alleen als ijkpunt. */
  indexFactor: z.number().positive().max(3).nullable().default(null),
  /** Zelfde complex of blok - dan is hij kandidaat voor het anker. */
  zelfdeComplex: z.boolean().default(false),
  /** Heeft hij foto's of tekst? Zonder allebei is hij alleen een ijkpunt voor de €/m². */
  heeftBeeld: z.boolean().default(true),
  oordelen: z.partialRecord(kenmerkSchema, kenmerkOordeelSchema).default({}),
  /** Per kenmerk hoe vergelijkbaar, 4 heel tot 0 helemaal niet (MA-70). */
  vergelijkbaar: z.partialRecord(kenmerkSchema, vergelijkbaarSchema).default({}),
  /** Met de hand mee of niet; null volgt de drempel van de taxatie. */
  meenemen: z.boolean().nullable().default(null),
});
export type Referentie = z.infer<typeof referentieSchema>;
export type ReferentieInvoer = z.input<typeof referentieSchema>;

export const doelpandSchema = z.object({
  oppervlakteM2: geheel.positive().nullable().default(null),
  slaapkamers: geheel.min(0).max(20).nullable().default(null),
  buitenruimte: buitenruimteSchema.nullable().default(null),
  staat: staatSchema.nullable().default(null),
  energielabel: z.string().max(4).nullable().default(null),
  /** Lopende erfpachtcanon per jaar; 0 bij eigen grond of afgekocht. */
  canon: z.number().min(0).nullable().default(null),
  /** Verdieping van de voordeur; 0 is de begane grond. */
  verdieping: geheel.min(-1).max(60).nullable().default(null),
  notities: z.string().max(2000).default(''),
});
export type Doelpand = z.infer<typeof doelpandSchema>;

/* --- Stap 2: locatie en juridisch ------------------------------------------ */

export const SCORES = ['goed', 'redelijk', 'slecht'] as const;
export const scoreSchema = z.enum(SCORES);
export type Score = z.infer<typeof scoreSchema>;

/** Wat je over de plek zegt, zoals de scores voorin een taxatierapport. */
export const LOCATIE_SCORES = ['ligging', 'bereikbaarheid', 'parkeren'] as const;
export const LOCATIE_SCORE_LABELS: Record<(typeof LOCATIE_SCORES)[number], string> = {
  ligging: 'Ligging',
  bereikbaarheid: 'Bereikbaarheid',
  parkeren: 'Parkeren op straat',
};

export const locatieSchema = z.object({
  scores: z.partialRecord(z.enum(LOCATIE_SCORES), scoreSchema).default({}),
  /** Erfpacht zoals je het nakeek: soort, afgekocht tot, AB-jaar. Vrije tekst, één regel. */
  erfpacht: z.string().max(300).default(''),
  notities: z.string().max(2000).default(''),
});
export type Locatie = z.infer<typeof locatieSchema>;

export const afgewezenSchema = z.object({
  soort: referentieSoortSchema,
  id: z.uuid(),
  adres: z.string().max(200),
  reden: z.string().max(300),
});

export const TAXATIE_MAX_REFERENTIES = 12;

export const taxatieInvoerSchema = z.object({
  doel: doelpandSchema.default(doelpandSchema.parse({})),
  referenties: z.array(referentieSchema).max(TAXATIE_MAX_REFERENTIES).default([]),
  afgewezen: z.array(afgewezenSchema).max(200).default([]),
  extraKenmerken: z.array(z.enum(EXTRA_KENMERKEN)).default([]),
  /** Neem standaard mee vanaf deze vergelijkbaarheid (MA-70); 3 is "goed". */
  drempel: z.number().int().min(1).max(4).default(DREMPEL_STANDAARD),
  /** Schuiven op gevoel mag niet; hooguit ±3%, met één zin waarom. */
  afwijking: z
    .object({ pct: z.number().min(-3).max(3), reden: z.string().min(1).max(300) })
    .nullable()
    .default(null),
  onderbouwing: z.string().max(4000).default(''),
  locatie: locatieSchema.default(locatieSchema.parse({})),
  /**
   * Welke regels van stap 1 en 2 je zelf hebt nagekeken, bij hun sleutel
   * (`m2`, `grond`, `monument`, ...). Een vinkje zegt niet dat de bron gelijk
   * had, alleen dat iemand ernaar keek.
   */
  nagekeken: z.array(z.string().max(40)).max(60).default([]),
});
export type TaxatieInvoer = z.infer<typeof taxatieInvoerSchema>;

/* --- Banden ---------------------------------------------------------------- */

export type Band = { min: number; max: number; eenheid: 'pct' | 'euro' };

const BUITEN_NIVEAU: Record<Buitenruimte, number> = { geen: 0, balkon: 1, tuin_dakterras: 2 };
const LABELS = ['G', 'F', 'E', 'D', 'C', 'B', 'A', 'A+', 'A++', 'A+++', 'A++++'];

function labelStap(label: string | null | undefined): number | null {
  if (!label) return null;
  const index = LABELS.indexOf(label.trim().toUpperCase());
  return index < 0 ? null : Math.min(index, 6);
}

/**
 * De band waarbinnen de correctie mag schuiven, als grootte (zonder teken).
 * `verschil` is wat het kenmerk scheidt: een aantal kamers, staatsniveaus,
 * labelstappen, of de twee soorten buitenruimte.
 */
export function bandVoor(
  kenmerk: Kenmerk,
  verschil?: { stappen?: number; buitenruimte?: [Buitenruimte, Buitenruimte] },
): Band {
  const stappen = Math.abs(verschil?.stappen ?? 1);
  const eenheid = KENMERK_EENHEID[kenmerk];
  switch (kenmerk) {
    case 'm2':
      return { min: 0, max: 18, eenheid };
    case 'slaapkamers':
      return { min: 1 * stappen, max: 3 * stappen, eenheid };
    case 'buitenruimte': {
      const paar = verschil?.buitenruimte;
      if (!paar) return { min: 0, max: 10, eenheid };
      const [a, b] = paar.map((soort) => BUITEN_NIVEAU[soort]).sort() as [number, number];
      if (a === b) return { min: 0, max: 3, eenheid };
      if (a === 0 && b === 1) return { min: 3, max: 5, eenheid };
      if (a === 1 && b === 2) return { min: 4, max: 7, eenheid };
      return { min: 7, max: 10, eenheid };
    }
    case 'staat': {
      const heel = Math.floor(stappen);
      const half = stappen - heel >= 0.5 ? 1 : 0;
      return { min: 5 * heel + 2 * half, max: 8 * heel + 4 * half, eenheid };
    }
    case 'ligging':
      return { min: 0, max: 8, eenheid };
    case 'verdieping':
      return { min: 0, max: 2, eenheid };
    case 'energielabel':
      return { min: Math.min(stappen, 4), max: Math.min(stappen, 4), eenheid };
    case 'status':
      return { min: 0, max: 7, eenheid };
    case 'uitzicht':
      return { min: 0, max: 12, eenheid };
    case 'verkoopwijze':
      return { min: 0, max: 5, eenheid };
    case 'plafond':
      return { min: 0, max: 0, eenheid };
    case 'eigendom':
    case 'parkeren':
      return { min: 0, max: Number.POSITIVE_INFINITY, eenheid };
  }
}

/** Buiten alle banden mag hooguit 2%, met een vlag. */
export const BUITEN_BAND_MAX = 2;

/* --- Voorstellen uit de data ----------------------------------------------- */

/**
 * Wat de data zelf al zegt, als beginstand van een oordeel. De gebruiker mag
 * het binnen de band verschuiven; voor kenmerken die alleen met eigen ogen te
 * beoordelen zijn (staat, ligging, uitzicht) is er geen voorstel.
 */
export function voorstel(
  kenmerk: Kenmerk,
  doel: Doelpand,
  referentie: Pick<Referentie, 'oppervlakteM2' | 'prijssoort' | 'prijs'> & {
    slaapkamers?: number | null;
    buitenruimte?: Buitenruimte | null;
    energielabel?: string | null;
    canon?: number | null;
    dagenTeKoop?: number | null;
    prijsVerlaagd?: boolean;
  },
): KenmerkOordeel | null {
  const maak = (correctie: number, bron: 'data' | 'tekst' = 'data'): KenmerkOordeel => ({
    oordeel: correctie === 0 ? 'gelijk' : correctie < 0 ? 'beter' : 'slechter',
    correctie: rond1(correctie),
    bron,
    notitie: '',
  });
  switch (kenmerk) {
    case 'm2': {
      if (!doel.oppervlakteM2 || !referentie.oppervlakteM2) return null;
      const verschil = (doel.oppervlakteM2 - referentie.oppervlakteM2) / referentie.oppervlakteM2;
      return maak(0.7 * verschil * 100);
    }
    case 'slaapkamers': {
      if (doel.slaapkamers == null || referentie.slaapkamers == null) return null;
      return maak((doel.slaapkamers - referentie.slaapkamers) * 2);
    }
    case 'buitenruimte': {
      if (!doel.buitenruimte || !referentie.buitenruimte) return null;
      const band = bandVoor('buitenruimte', {
        buitenruimte: [doel.buitenruimte, referentie.buitenruimte],
      });
      const richting = Math.sign(
        BUITEN_NIVEAU[doel.buitenruimte] - BUITEN_NIVEAU[referentie.buitenruimte],
      );
      return maak(richting * ((band.min + band.max) / 2));
    }
    case 'energielabel': {
      if (doel.staat === 'nieuw') return null;
      const a = labelStap(doel.energielabel);
      const b = labelStap(referentie.energielabel);
      if (a == null || b == null) return null;
      const stappen = Math.max(-4, Math.min(4, a - b));
      return maak(stappen);
    }
    case 'eigendom': {
      if (doel.canon == null || referentie.canon == null) return null;
      return maak((referentie.canon - doel.canon) * 25);
    }
    case 'status': {
      if (referentie.prijssoort === 'onder_bod') return maak(-1);
      if (referentie.prijssoort === 'aanbod') {
        const lang = (referentie.dagenTeKoop ?? 0) > 90 || referentie.prijsVerlaagd === true;
        return maak(lang ? -7 : -4);
      }
      return maak(0);
    }
    default:
      return null;
  }
}

/**
 * Wat de data al zegt over hoe vergelijkbaar een referentie is (MA-70), voor de
 * kenmerken die te meten zijn: m², slaapkamers, buitenruimte en energielabel.
 * De rest (staat, ligging, ...) beoordeel je zelf.
 */
export function voorstelVergelijkbaar(
  doel: Doelpand,
  ref: {
    oppervlakteM2: number | null;
    slaapkamers: number | null;
    buitenruimte: Buitenruimte | null;
    energielabel: string | null;
  },
): Partial<Record<Kenmerk, number>> {
  const uit: Partial<Record<Kenmerk, number>> = {};
  if (doel.oppervlakteM2 && ref.oppervlakteM2) {
    const verschil = Math.abs(ref.oppervlakteM2 - doel.oppervlakteM2) / doel.oppervlakteM2;
    uit.m2 =
      verschil <= 0.05 ? 4 : verschil <= 0.1 ? 3 : verschil <= 0.15 ? 2 : verschil <= 0.25 ? 1 : 0;
  }
  if (doel.slaapkamers != null && ref.slaapkamers != null) {
    const verschil = Math.abs(ref.slaapkamers - doel.slaapkamers);
    uit.slaapkamers = verschil === 0 ? 4 : verschil === 1 ? 2 : 0;
  }
  if (doel.buitenruimte && ref.buitenruimte) {
    const stappen = Math.abs(BUITEN_NIVEAU[ref.buitenruimte] - BUITEN_NIVEAU[doel.buitenruimte]);
    uit.buitenruimte = stappen === 0 ? 4 : stappen === 1 ? 2 : 1;
  }
  const a = doel.energielabel ? LABELS.indexOf(doel.energielabel.toUpperCase()) : -1;
  const b = ref.energielabel ? LABELS.indexOf(ref.energielabel.toUpperCase()) : -1;
  if (a >= 0 && b >= 0) uit.energielabel = Math.max(0, 4 - Math.abs(a - b));
  return uit;
}

/* --- Rekenen --------------------------------------------------------------- */

export type Gelijkenis = 'hoog' | 'middel' | 'laag';

export type ReferentieUitkomst = {
  id: string;
  /** Prijs na index, voor correcties. */
  basisprijs: number;
  gecorrigeerd: number;
  /** Alle posten zonder teken opgeteld, euro's als procent van de prijs. */
  totaleCorrectie: number;
  gelijkenis: Gelijkenis;
  /** Telt alleen als controle op de €/m². */
  ijkpunt: boolean;
  vlaggen: string[];
  /** Gemiddelde vergelijkbaarheid van de beoordeelde kenmerken, 0 tot 4; null als er nog niets is. */
  vergelijkbaar: number | null;
  /** Telt mee in de waarde: met de hand, of omdat hij de drempel haalt. */
  meegenomen: boolean;
  /** Prijs per m² (na index) maal de m² van het doelpand. */
  opDoel: number | null;
};

const MAAND_MS = 30.44 * 24 * 3600 * 1000;

function rond1(waarde: number): number {
  return Math.round(waarde * 10) / 10;
}

export function rekenReferentie(
  referentie: Referentie,
  doel: Doelpand,
  actief: readonly Kenmerk[],
  peildatum: Date,
  drempel: number = DREMPEL_STANDAARD,
): ReferentieUitkomst {
  const vlaggen: string[] = [];
  let ijkpunt = false;

  let basisprijs = referentie.prijs;
  if (referentie.datum) {
    const maanden = (peildatum.getTime() - Date.parse(referentie.datum)) / MAAND_MS;
    if (maanden > 12) {
      if (referentie.indexFactor) basisprijs = referentie.prijs * referentie.indexFactor;
      else {
        ijkpunt = true;
        vlaggen.push('Ouder dan 12 maanden en geen index: alleen ijkpunt.');
      }
    }
  }
  if (!referentie.heeftBeeld) {
    ijkpunt = true;
    vlaggen.push("Geen foto's en geen tekst: alleen ijkpunt.");
  }

  let pct = 0;
  let euro = 0;
  let totaal = 0;
  let onbekend = false;
  for (const kenmerk of actief) {
    const oordeel = referentie.oordelen[kenmerk];
    if (!oordeel || oordeel.oordeel === 'onbekend') {
      if (KERNKENMERKEN.includes(kenmerk as Kernkenmerk) && kenmerk !== 'status') onbekend = true;
      continue;
    }
    if (KENMERK_EENHEID[kenmerk] === 'euro') {
      euro += oordeel.correctie;
      totaal += (Math.abs(oordeel.correctie) / basisprijs) * 100;
    } else {
      pct += oordeel.correctie;
      totaal += Math.abs(oordeel.correctie);
      const band = bandVoor(kenmerk);
      if (Math.abs(oordeel.correctie) > band.max + BUITEN_BAND_MAX) {
        vlaggen.push(`${KENMERK_LABELS[kenmerk]}: ${oordeel.correctie}% valt buiten de band.`);
      }
    }
  }

  const gecorrigeerd = basisprijs * (1 + pct / 100) + euro;
  let gelijkenis: Gelijkenis = totaal <= 10 ? 'hoog' : totaal <= 25 ? 'middel' : 'laag';

  if (doel.oppervlakteM2 && referentie.oppervlakteM2) {
    const verschil =
      Math.abs(doel.oppervlakteM2 - referentie.oppervlakteM2) / referentie.oppervlakteM2;
    if (verschil > 0.25) {
      gelijkenis = 'laag';
      vlaggen.push('Meer dan 25% verschil in m²: geen referentie.');
    } else if (verschil > 0.15 && gelijkenis === 'hoog') gelijkenis = 'middel';
  }
  if (onbekend && gelijkenis === 'hoog') gelijkenis = 'middel';
  if (ijkpunt) gelijkenis = 'laag';

  const standen = actief
    .map((kenmerk) => referentie.vergelijkbaar[kenmerk])
    .filter((stand): stand is number => stand != null);
  // Het gemiddelde, maar nooit meer dan één stap boven het zwakste kenmerk: een
  // woning die op één punt weinig lijkt, is daarmee hooguit matig vergelijkbaar.
  const vergelijkbaar =
    standen.length > 0
      ? rond1(
          Math.min(standen.reduce((a, b) => a + b, 0) / standen.length, Math.min(...standen) + 1),
        )
      : null;
  const opDoel =
    doel.oppervlakteM2 && referentie.oppervlakteM2
      ? (basisprijs / referentie.oppervlakteM2) * doel.oppervlakteM2
      : null;
  const meegenomen =
    opDoel != null &&
    (referentie.meenemen ?? (vergelijkbaar != null && Math.round(vergelijkbaar) >= drempel));

  return {
    id: referentie.id,
    basisprijs: Math.round(basisprijs),
    gecorrigeerd: Math.round(gecorrigeerd),
    totaleCorrectie: rond1(totaal),
    gelijkenis,
    ijkpunt,
    vlaggen,
    vergelijkbaar,
    meegenomen,
    opDoel: opDoel == null ? null : Math.round(opDoel),
  };
}

/** Sinds MA-70 altijd `gewogen` of `geen`; de andere twee staan nog in oudere uitkomsten. */
export type TaxatieMethode = 'gewogen' | 'anker' | 'kleinste-drie' | 'geen';

export type TaxatieUitkomst = {
  referenties: ReferentieUitkomst[];
  methode: TaxatieMethode;
  /** De referenties waar de waarde op rust: de meegenomen. */
  basisIds: string[];
  /** Het gewogen gemiddelde, vóór de afwijking. */
  basis: number | null;
  /** Gewogen naar vergelijkbaarheid. */
  waarde: number | null;
  /** De ondergrens: de laagste meegenomen referentie, op het doelpand omgerekend. */
  laag: number | null;
  /** De bovengrens: de hoogste meegenomen referentie. */
  hoog: number | null;
  /** Mediaan €/m² van de transactie-achtige referenties, als controle. */
  controleM2: number | null;
  aantalTransacties: number;
  /** Zolang er geen echte koopsom tussen zit, zegt het A4 dat. */
  alleenVraagprijzen: boolean;
  vlaggen: string[];
};

export function mediaan(waarden: readonly number[]): number | null {
  if (waarden.length === 0) return null;
  const gesorteerd = [...waarden].sort((a, b) => a - b);
  const midden = Math.floor(gesorteerd.length / 2);
  return gesorteerd.length % 2 === 1
    ? (gesorteerd[midden] as number)
    : ((gesorteerd[midden - 1] as number) + (gesorteerd[midden] as number)) / 2;
}

function rondAf(waarde: number, op = 5000): number {
  return Math.round(waarde / op) * op;
}

/** Welke kenmerken in de tabel staan: de kern plus wat de gebruiker toevoegde. */
export function actieveKenmerken(invoer: Pick<TaxatieInvoer, 'extraKenmerken'>): Kenmerk[] {
  return [...KERNKENMERKEN, ...invoer.extraKenmerken];
}

/**
 * De taxatie (MA-70): elke meegenomen referentie geeft haar prijs per m² maal de
 * m² van het doelpand. De waarde is het gemiddelde daarvan, gewogen naar hoe
 * vergelijkbaar ze is; de ondergrens is de laagste, de bovengrens de hoogste.
 * Meegenomen is wie je met de hand aanvinkt, of wie de drempel haalt.
 */
export function rekenTaxatie(invoer: TaxatieInvoer, peildatum: Date): TaxatieUitkomst {
  const actief = actieveKenmerken(invoer);
  const uitkomsten = invoer.referenties.map((referentie) =>
    rekenReferentie(referentie, invoer.doel, actief, peildatum, invoer.drempel),
  );
  const perId = new Map(invoer.referenties.map((referentie) => [referentie.id, referentie]));
  const naam = (uitkomst: ReferentieUitkomst) => perId.get(uitkomst.id)?.adres ?? uitkomst.id;
  const vlaggen = uitkomsten.flatMap((uitkomst) =>
    uitkomst.vlaggen.map((vlag) => `${naam(uitkomst)}: ${vlag}`),
  );
  const transactieAchtig = (uitkomst: ReferentieUitkomst) => {
    const referentie = perId.get(uitkomst.id);
    return referentie ? isTransactieAchtig(referentie.prijssoort) : false;
  };

  const mee = uitkomsten.filter((uitkomst) => uitkomst.meegenomen && uitkomst.opDoel != null);
  // Met de hand meegenomen zonder oordeel telt als matig; allemaal "niet" telt gewoon gelijk.
  const gewicht = (uitkomst: ReferentieUitkomst) => uitkomst.vergelijkbaar ?? 2;
  const som = mee.reduce((totaal, uitkomst) => totaal + gewicht(uitkomst), 0);
  const gewogen =
    mee.length === 0
      ? null
      : som > 0
        ? mee.reduce(
            (totaal, uitkomst) => totaal + gewicht(uitkomst) * (uitkomst.opDoel as number),
            0,
          ) / som
        : mee.reduce((totaal, uitkomst) => totaal + (uitkomst.opDoel as number), 0) / mee.length;
  const opDoelen = mee.map((uitkomst) => uitkomst.opDoel as number);
  const waarde =
    gewogen == null ? null : rondAf(gewogen * (1 + (invoer.afwijking?.pct ?? 0) / 100));

  const doelM2 = invoer.doel.oppervlakteM2;
  const controleM2 = doelM2
    ? mediaan(mee.filter(transactieAchtig).map((uitkomst) => (uitkomst.opDoel as number) / doelM2))
    : null;
  const aantalTransacties = mee.filter(transactieAchtig).length;

  if (invoer.referenties.length > 0 && !doelM2) {
    vlaggen.push('Het doelpand heeft geen m²: zonder kan er niets worden omgerekend.');
  }
  for (const uitkomst of uitkomsten) {
    if (uitkomst.vergelijkbaar == null && perId.get(uitkomst.id)?.meenemen == null) {
      vlaggen.push(`${naam(uitkomst)}: nog niet beoordeeld, telt nog niet mee.`);
    }
  }
  if (invoer.referenties.length > 0 && mee.length > 0 && aantalTransacties < 2) {
    vlaggen.push('Minder dan 2 echte verkopen meegenomen.');
  }

  return {
    referenties: uitkomsten,
    methode: mee.length > 0 ? 'gewogen' : 'geen',
    basisIds: mee.map((uitkomst) => uitkomst.id),
    basis: gewogen == null ? null : Math.round(gewogen),
    waarde,
    laag: opDoelen.length > 0 ? rondAf(Math.min(...opDoelen), 1000) : null,
    hoog: opDoelen.length > 0 ? rondAf(Math.max(...opDoelen), 1000) : null,
    controleM2: controleM2 == null ? null : Math.round(controleM2),
    aantalTransacties,
    alleenVraagprijzen: !invoer.referenties.some(
      (referentie) => referentie.prijssoort === 'transactie',
    ),
    vlaggen,
  };
}

/* --- Wat de API teruggeeft -------------------------------------------------- */

export const taxatieSchema = z.object({
  id: z.uuid(),
  woningId: z.uuid(),
  invoer: taxatieInvoerSchema,
  waarde: geheel.nullable(),
  laag: geheel.nullable(),
  hoog: geheel.nullable(),
  bijgewerktOp: z.string(),
  bijgewerktDoor: z.string().nullable(),
});
export type Taxatie = z.infer<typeof taxatieSchema>;

const gelijkenisSchema = z.enum(['hoog', 'middel', 'laag']);

export const taxatieUitkomstSchema = z.object({
  referenties: z.array(
    z.object({
      id: z.string(),
      basisprijs: z.number(),
      gecorrigeerd: z.number(),
      totaleCorrectie: z.number(),
      gelijkenis: gelijkenisSchema,
      ijkpunt: z.boolean(),
      vlaggen: z.array(z.string()),
      vergelijkbaar: z.number().nullable(),
      meegenomen: z.boolean(),
      opDoel: z.number().nullable(),
    }),
  ),
  methode: z.enum(['gewogen', 'anker', 'kleinste-drie', 'geen']),
  basisIds: z.array(z.string()),
  basis: z.number().nullable(),
  waarde: z.number().nullable(),
  laag: z.number().nullable(),
  hoog: z.number().nullable(),
  controleM2: z.number().nullable(),
  aantalTransacties: z.number(),
  alleenVraagprijzen: z.boolean(),
  vlaggen: z.array(z.string()),
}) satisfies z.ZodType<TaxatieUitkomst>;

export const taxatieMetUitkomstSchema = z.object({
  taxatie: taxatieSchema.nullable(),
  uitkomst: taxatieUitkomstSchema.nullable(),
});
export type TaxatieMetUitkomst = z.infer<typeof taxatieMetUitkomstSchema>;
